import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Trash2, BookOpen, Users, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { Badge } from "../components/ui/badge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_LEVELS = [
  "ECDA", "ECDB",
  "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", "Grade 6", "Grade 7",
];

const getGradeColor = (grade) => {
  if (grade.startsWith("ECD")) return "bg-purple-50 text-purple-700 border-purple-200";
  if (["Grade 1", "Grade 2"].includes(grade)) return "bg-blue-50 text-blue-700 border-blue-200";
  return "bg-emerald-50 text-emerald-700 border-emerald-200";
};

export default function ClassesPage() {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    grade_level: "",
    name: "",
    description: "",
    year: new Date().getFullYear(),
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API}/classes`);
      setClasses(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      toast.error("Failed to fetch classes");
      setClasses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.grade_level) {
      toast.error("Please select a grade level");
      return;
    }
    if (!formData.name.trim()) {
      toast.error("Please enter a section name (e.g. A, B, C)");
      return;
    }
    try {
      await axios.post(`${API}/classes`, formData);
      toast.success(`Class "${formData.grade_level} ${formData.name}" created successfully`);
      setIsModalOpen(false);
      resetForm();
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to create class");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this class?")) return;
    try {
      await axios.delete(`${API}/classes/${id}`);
      toast.success("Class deleted successfully");
      fetchClasses();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete class");
    }
  };

  const resetForm = () => {
    setFormData({
      grade_level: "",
      name: "",
      description: "",
      year: new Date().getFullYear(),
    });
  };

  const getOrderedStudents = (students) => {
    return [...students].sort((a, b) => {
      const genderRankA = a?.gender === "Female" ? 0 : 1;
      const genderRankB = b?.gender === "Female" ? 0 : 1;

      if (genderRankA !== genderRankB) return genderRankA - genderRankB;

      const nameA = `${a?.last_name || ""} ${a?.first_name || ""}`.trim().toLowerCase();
      const nameB = `${b?.last_name || ""} ${b?.first_name || ""}`.trim().toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  const buildClassListHtml = (cls, students, subjects) => {
    const orderedStudents = getOrderedStudents(students);
    const girlCount = orderedStudents.filter(s => s.gender === 'Female').length;
    const schoolName = 'Cresabel';
    const classTitle = `${cls.display_name || cls.name || 'Class List'} — Class List ${cls.year}`;
    const logoMarkup = '<img src="https://cresabel.co.zw/Cresabel%20Logo.png" alt="Cresabel logo" style="width:48px;height:48px;object-fit:contain;border:1px solid #000;" />';
    const subjectCols = subjects.map(s => `<th style="border:1px solid #000;padding:2px 1px;text-align:center;font-size:6px;line-height:1.0;white-space:normal;word-break:break-word;min-width:22px;">${(s.name || '').replace(/&/g, '&amp;')}</th>`).join('');

    const studentRows = orderedStudents.map((s, idx) => {
      const isLastGirl = (idx === girlCount - 1 && girlCount > 0);
      const borderBottom = isLastGirl ? 'border-bottom:3px double #000;' : '';
      return `<tr>
        <td style="border:1px solid #000;padding:2px 1px;text-align:center;font-size:7px;line-height:1.0;${borderBottom}">${idx + 1}</td>
        <td style="border:1px solid #000;padding:2px 3px;white-space:nowrap;font-size:10px;line-height:1.1;min-width:140px;${borderBottom}">${s.last_name} ${s.first_name}</td>
        <td style="border:1px solid #000;padding:2px 1px;text-align:center;font-size:7px;line-height:1.0;${borderBottom}">${s.gender === 'Female' ? 'F' : 'M'}</td>
        ${subjects.map(() => `<td style="border:1px solid #000;padding:2px 1px;min-width:22px;text-align:center;font-size:6px;line-height:1.0;${borderBottom}">&nbsp;</td>`).join('')}
        <td style="border:1px solid #000;padding:2px 1px;min-width:24px;font-weight:bold;font-size:7px;line-height:1.0;${borderBottom}">&nbsp;</td>
        <td style="border:1px solid #000;padding:2px 1px;min-width:22px;font-size:7px;line-height:1.0;${borderBottom}">&nbsp;</td>
      </tr>`;
    }).join('');

    return `<!DOCTYPE html>
<html><head><title>Class List - ${cls.display_name || cls.name || 'Class List'}</title>
<style>
  @page { size: A4 portrait; margin: 4mm; }
  body { font-family: Arial, sans-serif; font-size: 7px; color: #000; }
  table { border-collapse: collapse; width: 100%; table-layout: fixed; }
  tr { height: 8px; }
  td, th { line-height: 1.0; }
  @media print { body { -webkit-print-color-adjust: exact; } }
</style>
</head><body>
<div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;padding-bottom:6px;border-bottom:1px solid #000;">
  <div style="display:flex;align-items:center;gap:8px;">
    ${logoMarkup}
    <div>
      <div style="font-size:12px;font-weight:bold;">${schoolName}</div>
      <div style="font-size:9px;margin-top:2px;">${classTitle}</div>
    </div>
  </div>
  <div style="text-align:right;font-size:7px;line-height:1.3;">
    <div>Girls: ${girlCount}</div>
    <div>Boys: ${students.length - girlCount}</div>
    <div>Total: ${students.length}</div>
  </div>
</div>
<table>
  <thead>
    <tr style="background:#f5f5f5;">
      <th style="border:1px solid #000;padding:2px 1px;text-align:center;width:22px;font-size:6px;">#</th>
      <th style="border:1px solid #000;padding:2px 3px;text-align:left;font-size:8px;min-width:140px;">Name</th>
      <th style="border:1px solid #000;padding:2px 1px;text-align:center;width:18px;font-size:6px;">G</th>
      ${subjectCols}
      <th style="border:1px solid #000;padding:2px 1px;text-align:center;width:24px;font-size:6px;">Tot</th>
      <th style="border:1px solid #000;padding:2px 1px;text-align:center;width:22px;font-size:6px;">Pos</th>
    </tr>
  </thead>
  <tbody>
    ${studentRows}
  </tbody>
</table>
<div style="margin-top:8px;font-size:7px;border-top:1px solid #000;padding-top:4px;">
  <p style="margin:0;">Teacher: __________________________ &nbsp;&nbsp; Date: __________________________</p>
</div>
</body></html>`;
  };

  const handlePrintClassList = async (classId) => {
    try {
      toast.info("Preparing class list...");
      const res = await axios.get(`${API}/classes/${classId}/print-data`);
      const cls = res.data?.class || {};
      const students = Array.isArray(res.data?.students) ? res.data.students : [];
      const subjects = Array.isArray(res.data?.subjects) ? res.data.subjects : [];

      const html = buildClassListHtml(cls, students, subjects);

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast.error("Please allow pop-ups to print the class list");
        return;
      }

      printWindow.document.write(html);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 400);
    } catch (error) {
      toast.error("Failed to load class print data");
    }
  };

  const handleDownloadClassList = async (classId) => {
    try {
      toast.info("Preparing class list Excel export...");
        const res = await axios.get(`${API}/classes/${classId}/print-data`);
      const cls = res.data?.class || {};
      const students = Array.isArray(res.data?.students) ? res.data.students : [];
      const subjects = Array.isArray(res.data?.subjects) ? res.data.subjects : [];
      const orderedStudents = getOrderedStudents(students);

      const rows = [];
      rows.push(['School', 'Cresabel']);
      rows.push(['Class', cls.display_name || cls.name || 'Class List']);
      rows.push([]);
      const headers = ['No.', 'Student Name', 'Gender'];
      subjects.forEach((subject) => headers.push(subject.name || 'Subject'));
      headers.push('Total', 'Position');
      rows.push(headers);

      orderedStudents.forEach((student, index) => {
        const row = [index + 1, `${student.last_name || ''} ${student.first_name || ''}`.trim(), student.gender === 'Female' ? 'F' : 'M'];
        subjects.forEach(() => row.push(''));
        row.push('', '');
        rows.push(row);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      worksheet['!page'] = { orientation: 'portrait', paperSize: 9, fitToWidth: 1, fitToHeight: 1 };
      worksheet['!margins'] = { left: 0.25, right: 0.25, top: 0.25, bottom: 0.25, header: 0.2, footer: 0.2 };
      worksheet['!cols'] = [
        { wch: 6 },
        { wch: 35 },
        { wch: 8 },
        ...Array.from({ length: subjects.length }, () => ({ wch: 10 })),
        { wch: 8 },
        { wch: 8 },
      ];

      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      for (let row = range.s.r; row <= range.e.r; row += 1) {
        for (let col = range.s.c; col <= range.e.c; col += 1) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: col });
          if (!worksheet[cellRef]) continue;

          if (!worksheet[cellRef].s) {
            worksheet[cellRef].s = {};
          }

          worksheet[cellRef].s = {
            ...worksheet[cellRef].s,
            border: {
              top: { style: 'thin', color: { rgb: '000000' } },
              bottom: { style: 'thin', color: { rgb: '000000' } },
              left: { style: 'thin', color: { rgb: '000000' } },
              right: { style: 'thin', color: { rgb: '000000' } },
            },
            alignment: {
              ...worksheet[cellRef].s?.alignment,
              vertical: 'center',
              wrapText: true,
            },
            font: {
              ...worksheet[cellRef].s?.font,
              sz: row === 0 ? 11 : 10,
            },
          };

          if (row === 0) {
            worksheet[cellRef].s.font = {
              ...worksheet[cellRef].s.font,
              bold: true,
            };
          }
        }
      }

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, (cls.display_name || cls.name || 'Class List').substring(0, 31));

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8;'
      });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${(cls.display_name || cls.name || 'class-list').replace(/\s+/g, '-').toLowerCase()}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      toast.success("Class list exported as Excel file");
    } catch (error) {
      toast.error("Failed to export class list");
    }
  };

  // Group classes by grade level for organized display
  const groupedClasses = {};
  GRADE_LEVELS.forEach((gl) => {
    const match = classes.filter((c) => c.grade_level === gl);
    if (match.length > 0) groupedClasses[gl] = match;
  });

  return (
    <div data-testid="classes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
            <BookOpen className="text-pink-500" size={32} />
            Classes
          </h1>
          <p className="text-slate-500 mt-1">Manage class sections by grade level</p>
        </div>

        {user?.role === "admin" && (
          <Dialog open={isModalOpen} onOpenChange={(open) => {
            setIsModalOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-pink-500 hover:bg-pink-600 text-white btn-press" data-testid="add-class-btn">
                <Plus size={18} className="mr-2" />
                Add Class
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[400px]">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Class</DialogTitle>
                <DialogDescription>
                  Select a grade level and section name
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Grade Level *</Label>
                  <Select value={formData.grade_level} onValueChange={(val) => setFormData({ ...formData, grade_level: val })}>
                    <SelectTrigger data-testid="class-grade-level">
                      <SelectValue placeholder="Select grade level" />
                    </SelectTrigger>
                    <SelectContent>
                      {GRADE_LEVELS.map((gl) => (
                        <SelectItem key={gl} value={gl}>{gl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Section Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value.toUpperCase() })}
                    placeholder="e.g. A, B, C"
                    required
                    data-testid="class-name"
                    maxLength={10}
                  />
                  {formData.grade_level && formData.name && (
                    <p className="text-sm text-pink-600 font-medium">
                      Class will be: {formData.grade_level} {formData.name}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g. Science stream"
                    data-testid="class-description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Academic Year</Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                    data-testid="class-year"
                  />
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsModalOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-pink-500 hover:bg-pink-600" data-testid="save-class-btn">
                    Create Class
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Classes Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
        </div>
      ) : classes.length === 0 ? (
        <Card className="border-slate-200">
          <CardContent className="text-center py-12 text-slate-500">
            <BookOpen size={48} className="mx-auto mb-4 opacity-50" />
            <p>No classes created yet</p>
            {user?.role === "admin" && (
              <p className="text-sm">Click "Add Class" to get started</p>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedClasses).map(([gradeLevel, gradeClasses]) => (
            <div key={gradeLevel}>
              <h2 className="font-heading font-semibold text-lg text-slate-700 mb-3 flex items-center gap-2">
                <Badge className={getGradeColor(gradeLevel)}>{gradeLevel}</Badge>
                <span className="text-sm text-slate-400">({gradeClasses.length} {gradeClasses.length === 1 ? "section" : "sections"})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {gradeClasses.map((cls) => {
                  const studentCount = Number(cls.student_count ?? 0);
                  const hasStudents = studentCount > 0;

                  return (
                    <Card key={cls.id} className="border-slate-200 card-hover" data-testid={`class-card-${cls.id}`}>
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="font-heading text-lg text-slate-900">
                              {cls.display_name || `${cls.grade_level} ${cls.name}`}
                            </CardTitle>
                            <p className="text-sm text-slate-500 mt-1">
                              Academic Year: {cls.year}
                            </p>
                          </div>
                          {user?.role === "admin" && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(cls.id)} className="text-slate-400 hover:text-red-500" data-testid={`delete-class-${cls.id}`}>
                              <Trash2 size={16} />
                            </Button>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {cls.description && (
                          <p className="text-slate-600 text-sm mb-4">{cls.description}</p>
                        )}
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-2 text-slate-500">
                            <Users size={16} />
                            <span className="text-sm">
                              {studentCount} {studentCount === 1 ? "student" : "students"}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadClassList(cls.id)}
                              className="text-slate-500 hover:text-pink-600 hover:border-pink-300"
                              title={hasStudents ? "Download class list" : "Download class list (empty class)"}
                              disabled={!hasStudents}
                            >
                              <Download size={14} className="mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
